import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/router';
import { useTextureTokenExtraction } from './useTextureTokenExtraction';
import {
  storeTextureToken,
  getStoredTextureToken,
  clearStoredTextureToken
} from './textureTokenExtractor';

// Context for managing texture token authentication
const TextureAuthContext = createContext();

export function TextureAuthProvider({ children }) {
  const router = useRouter();
  const [textureToken, setTextureToken] = useState(null);
  const [isTextureAuth, setIsTextureAuth] = useState(false);
  const [hasExtractedToken, setHasExtractedToken] = useState(false);

  // Determine extraction method from URL parameters
  const authType = router.query?.authType || 'auth0';
  const tokenVia = router.query?.tokenVia;

  // Only use specific extraction if authType=texture AND tokenVia are specified
  const shouldUseSpecificExtraction = authType === 'texture' && tokenVia;
  const extractionMethod = shouldUseSpecificExtraction ? tokenVia : 'global';

  // Create a stable reference for the URL replace function
  const replaceUrl = useCallback((newUrl) => {
    router.replace(newUrl);
  }, [router]);

  const { token: extractedToken, isLoading } = useTextureTokenExtraction(
    extractionMethod,
    router.query,
    replaceUrl
  );

  useEffect(() => {
    // Handle extracted token from initial request
    if (extractedToken) {
      // Always use the newly extracted token, even if we had one before
      setTextureToken(extractedToken);
      setIsTextureAuth(true);
      setHasExtractedToken(true);
      storeTextureToken(extractedToken);
    } else if (!isLoading && !hasExtractedToken) {
      // If no token was extracted and we're done loading, check stored token
      const storedToken = getStoredTextureToken();
      if (storedToken) {
        setTextureToken(storedToken);
        setIsTextureAuth(true);
        setHasExtractedToken(true);
      }
    }
  }, [extractedToken, isLoading, hasExtractedToken]);

  const clearTextureAuth = () => {
    setTextureToken(null);
    setIsTextureAuth(false);
    setHasExtractedToken(false);
    clearStoredTextureToken();
  };

  const setTokenAndStore = (token) => {
    setTextureToken(token);
    setIsTextureAuth(!!token);
    setHasExtractedToken(!!token);
    if (token) {
      storeTextureToken(token);
    } else {
      clearStoredTextureToken();
    }
  };

  // Determine if texture auth should be used
  const shouldUseTextureAuth = authType === 'texture' || isTextureAuth || hasExtractedToken;

  return (
    <TextureAuthContext.Provider value={{
      textureToken,
      isTextureAuth,
      isLoading: shouldUseSpecificExtraction && isLoading,
      shouldUseTextureAuth,
      hasExtractedToken,
      clearTextureAuth,
      setTextureToken: setTokenAndStore,
    }}>
      {children}
    </TextureAuthContext.Provider>
  );
}

export function useTextureAuth() {
  const context = useContext(TextureAuthContext);
  if (!context) {
    throw new Error('useTextureAuth must be used within a TextureAuthProvider');
  }
  return context;
}
